import React, { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { css, Global } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Popover } from 'antd'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { isScreenReadOnlyForUi } from '@/libs/hotel-pms-permission'
import { hasSiteControllerTwoWayPlugin } from '@/libs/plugins'
import { AdminGuestRoomResponseType } from '@/models/admin-guest-room'
import { ReactComponent as IconSync } from '@/static/images/icon_sync.svg'
// TODO 準備中の一括登録／解除のタスクで解放
// import { ReactComponent as IconWrench } from '@/static/images/icon_wrench.svg'
import { SiteControllerStockBulkUpdateModal } from './site-controller-stock-bulk-update-modal'
import { PreparationBulkCreateModal } from './preparation-bulk-create-modal'

type BulkOperationMenuProps = {
  hotelGuestRooms: AdminGuestRoomResponseType['guestRoomInfo']
  onCompleted: () => void
}

type OpenedModal = 'stockUpdate' | 'preparation' | undefined

type MenuItem = {
  key: NonNullable<OpenedModal>
  // stroke に currentColor を使うため、img ではなくコンポーネントとして描画して文字色に追従させる
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  title: string
  description: string
  /** 契約・連携状況で使えない操作。項目は残してグレーアウトする */
  disabled?: boolean
}

/**
 * アサイン画面の一括操作メニュー（︙）。
 * 利用できない操作も項目自体は残し、契約・連携状況が分かるようグレーアウトで表示する。
 */
export const BulkOperationMenu: React.FC<BulkOperationMenuProps> = ({ hotelGuestRooms, onCompleted }) => {
  const { t } = useTranslation()
  const { plugins, permissions, permissionsLoaded, permissionsError } = useContext<AccountContextValue>(AccountContext)
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const [openedModal, setOpenedModal] = useState<OpenedModal>()

  const menuItems: MenuItem[] = [
    {
      key: 'stockUpdate',
      Icon: IconSync,
      disabled: !hasSiteControllerTwoWayPlugin(plugins),
      title: t('roomManagerBulk.MenuStockUpdate'),
      description: t('roomManagerBulk.MenuStockUpdateDescription'),
    },
    // TODO 準備中の一括登録／解除のタスクで解放
    // {
    //   key: 'preparation',
    //   Icon: IconWrench,
    //   title: t('roomManagerBulk.MenuPreparation'),
    //   description: t('roomManagerBulk.MenuPreparationDescription'),
    // },
  ]

  const openModal = (modal: NonNullable<OpenedModal>) => {
    setIsMenuOpen(false)
    setOpenedModal(modal)
  }

  const closeAndReload = () => {
    setOpenedModal(undefined)
    onCompleted()
  }

  // 閲覧のみの権限では一括操作をどれも実行できないため、メニュー自体を出さない
  if (isScreenReadOnlyForUi(permissions, 'guest_room', permissionsLoaded, permissionsError)) {
    return null
  }

  const menuContent = () => (
    <div css={menuStyle}>
      {menuItems.map(({ key, Icon, title, description, disabled }) => (
        <button key={key} type="button" css={menuItemStyle} disabled={disabled} onClick={() => openModal(key)}>
          <Icon />
          <div>
            <p>{title}</p>
            <span>{description}</span>
          </div>
        </button>
      ))}
    </div>
  )

  return (
    <>
      <Global
        styles={css({
          '.ant-popover-inner-content': { padding: 'unset' },
          '.ant-popover-arrow': { display: 'none' },
        })}
      />
      <Popover placement="bottomLeft" trigger="click" content={menuContent()} visible={isMenuOpen} onVisibleChange={setIsMenuOpen}>
        <div css={kebabIconStyle}>
          <img alt={t('roomManagerBulk.MenuLabel')} src={require('@/static/images/icon_other.svg')} />
        </div>
      </Popover>

      {openedModal === 'stockUpdate' &&
        createPortal(
          <SiteControllerStockBulkUpdateModal onCancel={() => setOpenedModal(undefined)} onCompleted={closeAndReload} />,
          document.body,
        )}
      {openedModal === 'preparation' &&
        createPortal(
          <PreparationBulkCreateModal
            hotelGuestRooms={hotelGuestRooms}
            onCancel={() => setOpenedModal(undefined)}
            onCompleted={closeAndReload}
          />,
          document.body,
        )}
    </>
  )
}

const kebabIconStyle = css({
  width: 30,
  height: 30,
  flex: '0 0 30px',
  borderRadius: '50%',
  cursor: 'pointer',
  ':hover': { backgroundColor: '#f2f2f2' },
})

const menuStyle = css({
  width: 290,
  padding: 5,
  backgroundColor: '#fff',
  borderRadius: 5,
  boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.1)',
})

const menuItemStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  width: '100%',
  padding: 9,
  border: 'none',
  borderRadius: 4,
  backgroundColor: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
  ':hover': { backgroundColor: '#f2f2f2' },
  ':disabled': {
    cursor: 'default',
    backgroundColor: 'transparent',
    'p, span': { color: '#cccccc' },
    svg: { color: '#cccccc' },
  },
  svg: {
    flex: '0 0 16px',
    marginTop: 1,
    color: '#f2a40b',
  },
  p: {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1.35,
    color: '#676767',
  },
  span: {
    display: 'block',
    marginTop: 3,
    fontSize: 10,
    lineHeight: 1.4,
    color: '#a3a3a3',
  },
})
